import {
  useEffect,
  useMemo,
  useState,
  type FC,
} from 'react';

import { IconDeviceFloppy } from '@tabler/icons-react';
import {
  ButtonField,
  InputField,
  Smokebreak,
  TextareaField,
} from '@wk/components-react16';

import type {
  Checkpoint,
  CheckpointParameter,
} from '@/domains/checkpoint/checkpoint.types';
import { CheckpointParameterType } from '@/domains/checkpoint/checkpoint.types';
import { getCheckpointVersion } from '@/lib/checkpointKeys';

interface SiteComplianceCheckpointConfigDrawerProps {
  checkpoint: Checkpoint | null;
  configurationId: string;
  isOpen: boolean;
  isReadOnly: boolean;
  onClose: () => void;
  onSaveParameters: (parameters: CheckpointParameter[]) => void;
}

const getParameterValidationMessage = ({
  checkpoint,
  parameter,
}: {
  checkpoint: Checkpoint | null;
  parameter: CheckpointParameter;
}) => {
  if (
    !checkpoint ||
    parameter.isOptional ||
    parameter.isRuntime ||
    !checkpoint.isScored ||
    checkpoint.exceptions?.length
  ) {
    return null;
  }

  const value = `${parameter.paramValue ?? ''}`.trim();

  if (!value) {
    return 'This field is required';
  }

  switch (parameter.paramType) {
    case CheckpointParameterType.Boolean:
      return value === 'true' || value === 'false'
        ? null
        : 'Use true or false';
    case CheckpointParameterType.Integer:
      return Number.isInteger(Number(value)) ? null : 'Use a valid integer';
    case CheckpointParameterType.Float:
      return Number.isNaN(Number(value)) ? 'Use a valid number' : null;
    default:
      return null;
  }
};

const SiteComplianceCheckpointConfigDrawer: FC<
  SiteComplianceCheckpointConfigDrawerProps
> = ({
  checkpoint,
  configurationId,
  isOpen,
  isReadOnly,
  onClose,
  onSaveParameters,
}) => {
  const [parameters, setParameters] = useState<CheckpointParameter[]>([]);

  useEffect(() => {
    setParameters(
      checkpoint?.parameters.map((parameter) => ({
        ...parameter,
        paramValue: parameter.paramValue ?? '',
      })) ?? [],
    );
  }, [checkpoint]);

  const checkpointVersion = getCheckpointVersion(checkpoint);

  const parameterErrors = useMemo(
    () =>
      parameters.map((parameter) =>
        getParameterValidationMessage({
          checkpoint,
          parameter,
        }),
      ),
    [checkpoint, parameters],
  );

  const hasValidationErrors = parameterErrors.some(Boolean);
  const hasException = Boolean(checkpoint?.exceptions?.length);

  return (
    <Smokebreak
      isOpen={isOpen}
      position="right"
      onUserRequest={onClose}
    >
      <h2 className="p-3 text-lg font-semibold" slot="smokebreakHeader">
        {checkpoint?.id}
        {checkpointVersion !== null ? ` (v${checkpointVersion})` : ''}
      </h2>
      <div className="flex w-full flex-col gap-4 p-3" slot="smokebreakBody">
        <InputField label="Configuration ID" size="small">
          <input disabled type="text" value={configurationId} />
        </InputField>
        <InputField label="Category" size="small">
          <input disabled type="text" value={checkpoint?.category ?? ''} />
        </InputField>
        <InputField label="Accountable (Division/Team)" size="small">
          <input disabled type="text" value={checkpoint?.accountable ?? ''} />
        </InputField>
        <InputField label="Owner" size="small">
          <input disabled type="text" value={checkpoint?.owner ?? ''} />
        </InputField>
        <InputField label="Systems" size="small">
          <input
            disabled
            type="text"
            value={checkpoint?.referenceSystem ?? ''}
          />
        </InputField>
        <TextareaField label="Checkpoint Description" size="small">
          <textarea disabled value={checkpoint?.description ?? ''} />
        </TextareaField>

        {parameters.length ? (
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">Parameters</h3>
            <form className="flex flex-col gap-2">
              {parameters.map((parameter, index) => (
                <InputField
                  key={parameter.paramName}
                  label={parameter.displayName}
                  size="small"
                  description={
                    parameter.isRuntime
                      ? 'This parameter is set at runtime and cannot be modified now'
                      : parameter.paramDescription
                  }
                  errors={
                    parameterErrors[index]
                      ? [
                          {
                            label: parameterErrors[index] ?? '',
                            labelId: parameter.paramName,
                          },
                        ]
                      : undefined
                  }
                >
                  <input
                    type="text"
                    value={parameter.paramValue}
                    disabled={isReadOnly || parameter.isRuntime}
                    onChange={(event) => {
                      const nextValue = event.target.value;

                      setParameters((currentParameters) =>
                        currentParameters.map((currentParameter, currentIndex) =>
                          currentIndex === index
                            ? {
                                ...currentParameter,
                                paramValue: nextValue,
                              }
                            : currentParameter,
                        ),
                      );
                    }}
                  />
                </InputField>
              ))}
              <ButtonField>
                <button
                  type="button"
                  onClick={() => onSaveParameters(parameters)}
                  disabled={isReadOnly || hasValidationErrors}
                >
                  <IconDeviceFloppy size={16} />
                  Save Parameters
                </button>
              </ButtonField>
            </form>
          </div>
        ) : null}

        {isReadOnly ? (
          <p className="text-sm text-gray-500">
            Parameters can be edited only while the configuration is in draft
            mode.
          </p>
        ) : null}

        {hasException ? (
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">Exception</h3>
            <TextareaField label="Exception Reason" size="small">
              <textarea
                disabled
                value={checkpoint?.exceptions?.[0]?.description ?? ''}
              />
            </TextareaField>
            <p className="text-sm text-gray-500">
              Created By: {checkpoint?.exceptions?.[0]?.createdBy ?? 'N/A'}
            </p>
            {checkpoint?.exceptions?.[0]?.isApproved === null ? (
              <p className="text-sm text-center text-gray-500">
                This exception is awaiting approval.
              </p>
            ) : null}
            {checkpoint?.exceptions?.[0]?.isApproved ? (
              <p className="text-sm text-center text-gray-500">
                Approved By: {checkpoint?.exceptions?.[0]?.approvedBy ?? 'N/A'}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </Smokebreak>
  );
};

export default SiteComplianceCheckpointConfigDrawer;
